import { useCallback, useEffect, useState } from "react";
import { useApi } from "../auth/AuthProvider";
import { useInvalidateOn } from "./useInvalidateOn";

export interface OrgDisplayAgent {
  id: string;
  hubId: string;
  venueId: string;
  venueName: string;
  venueSlug: string;
  name: string;
  /** "connected" / "offline" / etc. — from hub_agents.status, synced from
   *  the hub via entity-delta when the WS connects/disconnects. */
  status: string;
  /** When the hub last reported anything about this agent. null until first sync. */
  lastSeenAt: string | null;
  version: string | null;
  /** Whether the hub itself is currently online (`hub_status.online`). Even a
   *  display that thinks it's connected can't be reached if the hub is dark. */
  hubOnline: boolean;
}

/** Fetch all display-agents in an org from the cloud's hub_agents mirror.
 *  Used by the cross-venue Push to Displays modal. */
export function useOrgDisplayAgents(organisationId: string | undefined): {
  data: OrgDisplayAgent[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const api = useApi();
  const [data, setData] = useState<OrgDisplayAgent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [counter, setCounter] = useState(0);

  const refetch = useCallback(() => setCounter((c) => c + 1), []);

  useEffect(() => {
    if (!organisationId) {
      setData([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const res = await api(
          `/api/display-agents?organisationId=${encodeURIComponent(organisationId)}`,
        );
        const json = await res.json();
        if (!res.ok || !json.success) {
          throw new Error(json.error ?? `HTTP ${res.status}`);
        }
        if (!cancelled) {
          setData((json.data ?? []) as OrgDisplayAgent[]);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, organisationId, counter]);

  // Live invalidation: any `display-agent` status flip on a hub anywhere in
  // the org is broadcast to subscribed cloud-ui sessions via entity-changed.
  // Refetch so the modal's status badges update without an operator refresh.
  // Also refetch on `venue` changes — the row carries `hubOnline`, and a
  // hub going offline should grey the venue's displays immediately.
  useInvalidateOn("display-agent", refetch);
  useInvalidateOn("venue", refetch);

  return { data, loading, error, refetch };
}
